import {
  FC,
  Fragment,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ValidationResult,
  useForm,
  useFormParams,
  useSubscribe,
} from '@oresundsbron/use-form';
import { z } from 'zod';
import {
  ITicketProduct,
  privateITicketOrderV2,
} from '@oresundsbron/validators';
import { useTranslation } from 'next-i18next';
import { toCurrency } from '../../lib/currency';
import {
  Box,
  Input,
  LoadingButton,
  Select,
  Typography,
} from '@oresundsbron/bridge-ui';
import { FieldSet } from '../../components/Forms/Fieldset';
import { pipe } from 'fp-ts/lib/function';
import { useRouter } from 'next/router';
import { InputSearch } from '@oresundsbron/bridge-ui';
import {
  LicensePlateInfo,
  VehicleInfo,
} from '~/components/Forms/RegisterAlpr/types';
import { SupportedCountries } from '@oresundsbron/api/src/backend/VehicleInfo';
import { isBvSupportCountry } from '~/utils';
import { Banner } from '@oresundsbron/bridge-ui';
import { BackendError } from '@oresundsbron/api';
import {
  VehicleDataLabels,
  VehicleInformationDisplay,
} from './VehicleInformationDisplay';
import { useValidateAlpr } from '~/hooks/useValidateAlpr';
import { chain, fromPredicate, filter, match } from 'fp-ts/lib/TaskOption';
import TicketTypeCard from './TicketTypeCard';
import { ITicketType } from '@oresundsbron/validators';
import { Vehicles } from '~/lib/vehicle';
import {
  DayPickerComponent,
  DayPickerComponentRef,
} from './DayPickerComponent';
import { CountryCodeOptItem } from '~/hooks/useCountries';
import { useVehicleInformation } from '@oresundsbron/app-common';
export type Data = z.input<typeof privateITicketOrderV2>;

type SectionInformation = { title: string; description: ReactNode };

export type Sections = {
  car: SectionInformation;
  ticketType: Omit<SectionInformation, 'description'>;
  contactInformation: SectionInformation;
  news: SectionInformation;
  termsOfAgreement: SectionInformation & { orderTitle: string };
};

export type Labels = Omit<
  Record<
    | keyof Data
    | 'submit'
    | 'loading'
    | 'noVehicleInfo'
    | 'loadingSearch'
    | 'vehicleSearch'
    | 'make'
    | 'model'
    | 'color'
    | 'licensePlateExist',
    string
  >,
  'amount' | 'orderTitle' | 'refundUrl' | 'ticketTypeId' | 'fromDate' | 'toDate'
> & { total: string; chooseCountry: string };

export type OnlineTicketFormProps = {
  sections: Sections;
  labels: Labels;
  countryOptions: CountryCodeOptItem[];
  bvSupportCountries: SupportedCountries;
  ticketTypes: ITicketType[];
  vehicleColors: { label: string; value: string }[];
  fuelTypes: { label: string; value: string }[];
  emissionClasses: { label: string; value: string }[];
  error?: ReactNode;
  parentIsLoading?: boolean;
  vehicleInfoLabels: VehicleDataLabels;
  defaultVehicleColor: string;
} & Omit<useFormParams<Data>, 'namespace' | 'title'>;

type TicketPrice = ITicketProduct & { label: string };
export const OnlineTicketForm: FC<OnlineTicketFormProps> = ({
  labels,
  countryOptions,
  bvSupportCountries,
  sections,
  ticketTypes,
  defaultValues,
  onValidate,
  onSubmit,
  vehicleInfoLabels,
  vehicleColors,
  defaultVehicleColor,
  fuelTypes,
  emissionClasses,
}) => {
  const { locale } = useRouter();

  const { t } = useTranslation(['onlineTicket', 'common'], {
    nsMode: 'fallback',
  });
  const formRef = useRef<HTMLFormElement>(null);
  const [loading, toggleLoading] = useState(false);
  const namespace = 'onlineTicket';

  const {
    registerField,
    getError,
    handleSubmit,
    validate,
    isValid,
    setFieldErrors,
    setFieldData,
  } = useForm<Data>({
    namespace,
    defaultValues,
    onValidate,
    onSubmit,
  });

  const [vehicleError, setVehicleError] = useState<BackendError | null>();
  const [vehicleInfoSection, setVehicleInfoSection] =
    useState<VehicleInfo | null>();

  const [country, licensePlate] = useSubscribe<Data>(
    namespace,
    'country',
    'licensePlate'
  );
  const [plateInfo, setPlateInfo] = useState<LicensePlateInfo | undefined>();

  const {
    data: vehicleData,
    error: vehicleInforError,
    isFetching: isVehicleFetching,
  } = useVehicleInformation(plateInfo as LicensePlateInfo);

  useEffect(() => setVehicleError(vehicleInforError), [vehicleInforError]);
  useEffect(() => setVehicleError(null), [licensePlate]);
  useEffect(() => setVehicleInfoSection(vehicleData), [vehicleData]);
  useEffect(() => setVehicleInfoSection(null), [licensePlate, country]);
  useEffect(() => setPlateInfo(undefined), [country]);

  const validateAlpr = useValidateAlpr();

  const isBvSupported = useMemo(
    () => isBvSupportCountry(country as string, bvSupportCountries),
    [country, bvSupportCountries]
  );

  const [acceptedAgreement, vehicleOption, subscribe, fromDate, toDate] =
    useSubscribe<Data>(
      'onlineTicket',
      'agreement',
      'vehicleOption',
      'subscribe',
      'fromDate',
      'toDate'
    ) as string[];

  const [selectedTicketTypeRadio, setSelectedTicketTypeRadio] =
    useState<ITicketType | null>(null);

  const vehicleOptions = useMemo(() => {
    return Vehicles.reduce((acc, v) => {
      const price = selectedTicketTypeRadio?.prices.find(
        (p) => p.classId === v.classID
      );
      if (price) return [...acc, { ...price, label: t(v.label) }];
      else return acc;
    }, [] as TicketPrice[]);
  }, [selectedTicketTypeRadio?.prices, t]);

  const doublePrice = selectedTicketTypeRadio
    ? selectedTicketTypeRadio.useDoublePriceLine
    : false;

  const lines = useMemo(() => {
    const line = vehicleOptions.find(
      (v) =>
        v.classId === vehicleOption.split('-')[0] &&
        v.label === vehicleOption.split('-')[1]
    );
    if (!line || !selectedTicketTypeRadio) {
      return [];
    }
    const price = selectedTicketTypeRadio.useDiscountedPrice
      ? line.discountedInclVat
      : line.priceInclVat;

    const priceLine = {
      label: `${selectedTicketTypeRadio.title} - ${line.label}`,
      value: toCurrency(price, locale),
    };
    const lines = doublePrice ? [priceLine, priceLine] : [priceLine];

    const discount = doublePrice
      ? (line.priceInclVat - line.discountedInclVat) * 2
      : line.priceInclVat - line.discountedInclVat;

    const showDiscountedLines =
      selectedTicketTypeRadio.isEligableForNewsletterDiscount && !!subscribe;

    return showDiscountedLines
      ? [
          ...lines,
          {
            label: 'Rabatt',
            value: toCurrency(discount, locale),
            discount: true,
          },
        ]
      : lines;
  }, [
    vehicleOptions,
    selectedTicketTypeRadio,
    locale,
    doublePrice,
    subscribe,
    vehicleOption,
  ]);

  const total = useMemo(() => {
    const line = vehicleOptions.find(
      (v) =>
        v.classId === vehicleOption.split('-')[0] &&
        v.label === vehicleOption.split('-')[1]
    );
    if (!line || !selectedTicketTypeRadio) {
      return '';
    }

    const price = selectedTicketTypeRadio.useDiscountedPrice
      ? line.discountedInclVat
      : selectedTicketTypeRadio.isEligableForNewsletterDiscount && !!subscribe
      ? line.discountedInclVat
      : line.priceInclVat;

    return doublePrice
      ? toCurrency(price * 2, locale)
      : toCurrency(price, locale);
  }, [
    vehicleOptions,
    selectedTicketTypeRadio,
    subscribe,
    doublePrice,
    locale,
    vehicleOption,
  ]);

  const onSetPlate = useCallback(
    (plate: string) =>
      pipe(
        { licensePlate: plate, country: country as string },
        fromPredicate((x) => !!x.country && !!x.licensePlate),
        chain(
          validateAlpr<Data>({
            licensePlate: labels.licensePlateExist,
          })
        ),
        filter((v) => !!v),
        match(
          () =>
            setPlateInfo({ licensePlate: plate, country: country as string }),
          (errs) => {
            setFieldErrors(errs as ValidationResult<Data>);
            setPlateInfo(undefined);
          }
        )
      )(),
    [setPlateInfo, country, validateAlpr, labels, setFieldErrors]
  );

  const dayPickerRef = useRef<DayPickerComponentRef>(null);
  const dayPickerDivRef = useRef<HTMLDivElement>(null);
  const vehicleOptionSelectRef = useRef<HTMLSelectElement>(null);
  const countrySelectRef = useRef<HTMLSelectElement>(null);
  const licensePlateSelectRef = useRef<HTMLSelectElement>(null);
  const firstNameInputRef = useRef<HTMLInputElement>(null);
  const lastNameInputRef = useRef<HTMLInputElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const agreementInputRef = useRef<HTMLInputElement>(null);
  const refList = [
    vehicleOptionSelectRef,
    countrySelectRef,
    licensePlateSelectRef,
    firstNameInputRef,
    lastNameInputRef,
    emailInputRef,
    agreementInputRef,
  ];

  ////////// Ticket type selection

  const handleRadioChange = useCallback(
    (index: number) => {
      dayPickerRef.current?.setSelectedDays(undefined);
      setFieldData('fromDate', '', '');
      setFieldData('toDate', '', '');

      setSelectedTicketTypeRadio(ticketTypes[index]);
      setFieldData('ticketTypeId', ticketTypes[index]?.id, 'select');
      const amount = ticketTypes[index]?.useDoublePriceLine ? '2' : '1';
      setFieldData('amount', amount, '');
    },
    [setFieldData, ticketTypes]
  );

  useEffect(() => {
    if (ticketTypes?.length > 0) {
      handleRadioChange(0);
    }
  }, [ticketTypes, handleRadioChange]);
  const [hasValidDates, setHasValidDates] = useState<boolean>(true);

  const validateDates = () => {
    if (selectedTicketTypeRadio?.campaignEndDate && !toDate && !fromDate) {
      // This is a campaign ticket, and date selection is mandatory
      setHasValidDates(false);
      return false;
    }
    setHasValidDates(true);
    return true;
  };

  const scrollToError = (datesValid: boolean) => {
    if (!datesValid) {
      dayPickerDivRef.current?.scrollIntoView({
        behavior: 'smooth',
      });
    } else {
      refList
        .find((ref) => !ref.current?.validity.valid)
        ?.current?.scrollIntoView({
          behavior: 'smooth',
        });
    }
  };

  return (
    <>
      <Box
        as="form"
        onSubmit={handleSubmit}
        className="col-span-full"
        ref={formRef}
      >
        {ticketTypes.length > 0 && (
          <div className="mb-8 grid w-full grid-cols-1 gap-3 md:mb-16 md:grid-cols-2">
            {ticketTypes?.map((item, index) => (
              <Fragment key={item.id}>
                <TicketTypeCard
                  item={item}
                  index={index}
                  onRadioChange={handleRadioChange}
                  selectedValue={selectedTicketTypeRadio}
                />
              </Fragment>
            ))}
          </div>
        )}

        <DayPickerComponent
          ref={dayPickerRef}
          divRef={dayPickerDivRef}
          setFieldData={setFieldData}
          ticketType={selectedTicketTypeRadio?.ticketType}
          minDate={selectedTicketTypeRadio?.campaignStartDate}
          maxDate={selectedTicketTypeRadio?.campaignEndDate}
          hasValidDates={hasValidDates}
          setHasValidDates={setHasValidDates}
        />
        <FieldSet
          className="mb-16 border-b border-neutral-200 pb-16"
          title={sections.car.title}
          id="car"
          editButton={false}
          full={false}
        >
          <Box className="col-span-full">
            <Typography className="max-w-xl">
              {sections.car.description}
            </Typography>
          </Box>
          <Select
            ref={vehicleOptionSelectRef}
            className="col-span-full md:col-span-4"
            {...registerField('vehicleOption')}
            error={getError('vehicleOption')}
            label={labels.vehicleOption}
            options={vehicleOptions.map(({ label, classId }) => ({
              key: `${classId}-${label}`,
              label,
              value: `${classId}-${label}`,
            }))}
            placeholder={labels.vehicleOption}
            required
          />
          <Select
            ref={countrySelectRef}
            className="col-span-full md:col-span-4"
            {...registerField('country')}
            error={getError('country')}
            label={labels.country}
            groupedOptions={countryOptions.map((c) => ({
              children: c.children,
              group: { label: c.group?.label ? t(c.group.label) : undefined },
            }))}
            placeholder={labels.chooseCountry}
            required
          />
          {isBvSupported ? (
            <>
              <InputSearch
                ref={licensePlateSelectRef}
                className="col-span-full md:col-span-4"
                label={labels.licensePlate}
                button={{
                  isLoading: isVehicleFetching,
                  loadingLabel: labels.loadingSearch,
                  disabled: isVehicleFetching || !!getError('licensePlate'),
                }}
                buttonText={labels.vehicleSearch}
                error={getError('licensePlate')}
                input={registerField('licensePlate')}
                btnOnClick={onSetPlate}
                required
              />
              {!!vehicleInfoSection && (
                <VehicleInformationDisplay
                  labels={vehicleInfoLabels}
                  data={vehicleInfoSection}
                  extraData={{
                    vehicleColors,
                    defaultVehicleColor,
                    fuelTypes,
                    emissionClasses,
                  }}
                />
              )}
              {vehicleError ? (
                <Banner className="col-span-full">
                  {labels.noVehicleInfo}
                </Banner>
              ) : null}
            </>
          ) : (
            <Input
              ref={licensePlateSelectRef}
              className="col-span-full md:col-span-4"
              label={labels.licensePlate}
              {...registerField('licensePlate')}
              error={getError('licensePlate')}
              required
            />
          )}
        </FieldSet>
        <FieldSet
          className="col-span-full"
          title={sections.contactInformation.title}
          editButton={false}
          full={false}
          id="contact"
        >
          <Box className="col-span-full">
            <Typography className="max-w-xl">
              {sections.contactInformation.description}
            </Typography>
          </Box>
          <Input
            ref={firstNameInputRef}
            className="col-span-full sm:col-span-4"
            label={labels.firstName}
            {...registerField('firstName')}
            error={getError('firstName')}
            required
          />
          <Input
            ref={lastNameInputRef}
            className="col-span-full sm:col-span-4"
            label={labels.lastName}
            {...registerField('lastName')}
            error={getError('lastName')}
            required
          />
          <Input
            ref={emailInputRef}
            className="col-span-full sm:col-span-4"
            label={labels.email}
            {...registerField('email')}
            error={getError('email')}
            required
          />
          {selectedTicketTypeRadio?.isEligableForNewsletterDiscount && (
            <Box className="col-span-full mb-16 mt-4 border-b border-neutral-200 pb-16">
              <Typography as={'h3'} intent={'title'} size={'md'}>
                {sections.news.title}
              </Typography>

              <Typography className="mb-8 max-w-xl">
                {sections.news.description}
              </Typography>

              <Input
                className="col-span-full"
                label={labels.subscribe}
                {...registerField('subscribe', {
                  type: 'checkbox',
                  value: 'true',
                })}
                error={getError('subscribe')}
              />
            </Box>
          )}
        </FieldSet>

        <Typography intent="title">
          {sections.termsOfAgreement.orderTitle}
        </Typography>

        <Box className="col-span-full mb-6 mt-4 rounded-lg bg-blue-50 px-6 py-3">
          <Box as="ul">
            {lines.length > 0 ? (
              lines.map(({ label, value }, i) => (
                <Box
                  key={i}
                  as="li"
                  className="my-3 flex flex-row justify-between"
                >
                  <Typography className="font-semibold text-neutral-600">
                    {label}
                  </Typography>
                  <Typography>{value}</Typography>
                </Box>
              ))
            ) : (
              <Box as="li" className="my-3 flex flex-row justify-between">
                <Typography className="font-semibold italic text-neutral-600">
                  {t('form.sections.termsOfAgreement.pickACar')}
                </Typography>
              </Box>
            )}
            <Box
              as="li"
              className="flex flex-row justify-between border-t border-blue-200 py-3 text-blue-800"
            >
              <Typography weight="semibold">{labels.total}</Typography>
              <Typography weight="semibold">{total}</Typography>
            </Box>
          </Box>
        </Box>

        <Box>
          <Input
            ref={agreementInputRef}
            className="col-span-full"
            {...registerField('agreement', {
              value: 'true',
              type: 'checkbox',
            })}
            label={labels.agreement}
            error={getError('agreement')}
            required
          />
          <Typography className="col-span-full mt-4" intent="label">
            {sections.termsOfAgreement.description}
          </Typography>
        </Box>
        <Box className="col-span-full flex md:justify-end">
          <LoadingButton
            loadingLabel={labels.loading}
            isLoading={loading}
            type="submit"
            onClick={async (e) => {
              e.preventDefault();
              toggleLoading(true);
              const datesValid = validateDates();
              const valid = await validate()();
              toggleLoading(false);
              if (valid && datesValid) {
                formRef.current?.requestSubmit();
              } else {
                scrollToError(datesValid);
              }
            }}
            disabled={!isValid || acceptedAgreement !== 'true'}
          >
            {labels.submit}
          </LoadingButton>
        </Box>
      </Box>
    </>
  );
};
